import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useUserInfo } from '../../../../api/hooks/user/useUserInfo'
import { ScoopAvatar, ScoopText } from '../../../ui'
import { HelpBotAIAvatar } from '../HelpBotAIAvatar/HelpBotAIAvatar'
import styles from './HelpBotMessage.module.scss'

const DOCS_BASE_URL = 'https://docs.scoopanalytics.com/docs'

const processContent = (content) => {
    if (!content) return ''
    return content.replace(
        /<Image[^>]*src="([^"]*)"(?:[^>]*alt="([^"]*)")?[^>]*\/>/g,
        (match, src, alt) => {
            return `![${alt || ''}](${src})`
        }
    )
}

const resolveHref = (href) => {
    if (!href) return href

    // Already an absolute URL
    if (href.startsWith('http://') || href.startsWith('https://')) {
        return href
    }

    // Anchor link
    if (href.startsWith('#')) {
        return href
    }

    // Relative URL - convert to docs link
    // Remove leading ../ or ./
    const cleanPath = href.replace(/^(\.\.\/)+/, '').replace(/^\.\//, '')
    return `${DOCS_BASE_URL}/${cleanPath}`
}

export const HelpBotMessage = ({ message }) => {
    const isUser = message.role === 'user'
    const { user } = useUserInfo()

    const processedContent = useMemo(() => processContent(message.content), [message.content])

    return (
        <div className={`${styles.message} ${isUser ? styles.user : styles.assistant}`}>
            {isUser ? (
                <ScoopAvatar
                    firstName={user?.firstName}
                    lastName={user?.lastName}
                    style={{ width: 37, height: 37, fontSize: 14 }}
                />
            ) : (
                <HelpBotAIAvatar status={message.isError ? 'error' : 'idle'} size={37} />
            )}
            <div className={`${styles.bubble} ${message.isError ? styles.error : ''}`}>
                {isUser ? (
                    <ScoopText className={styles.textContent}>{message.content}</ScoopText>
                ) : (
                    <div className={styles.markdownContent}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                img: ({ src, alt }) => (
                                    <figure className={styles.figure}>
                                        <img
                                            src={src}
                                            alt={alt || ''}
                                            className={styles.image}
                                            loading="lazy"
                                        />
                                        {alt && (
                                            <figcaption className={styles.caption}>
                                                {alt}
                                            </figcaption>
                                        )}
                                    </figure>
                                ),
                                a: ({ href, children }) => (
                                    <a
                                        href={resolveHref(href)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {children}
                                    </a>
                                ),
                            }}
                        >
                            {processedContent}
                        </ReactMarkdown>
                    </div>
                )}
            </div>
        </div>
    )
}
